import { Injectable, NgZone } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'
import { JwtService } from '@app/modules/auth/jwt.service'
import { ApiService } from '@shared/services/api.service'
import { demuxFrame, normalizeAssetEventFrame, normalizeAssetFrame, normalizePositionFrame } from './realtime-frame'
import {
  TrackerEventPayload,
  TrackerHistoryPayload,
  TrackerHistoryPayloadAsset,
} from '@app/modules/client/map/shared/models/tracker-history-payload.model'

const STALE_MS = 70_000 // > server 30s ping; force reconnect if silent
const BACKOFF_MIN = 1_000
const BACKOFF_MAX = 30_000

@Injectable({ providedIn: 'root' })
export class GoRealtimeClient {
  private socket: WebSocket | null = null
  private backoff = BACKOFF_MIN
  private reconnectTimer: any = null
  private staleTimer: any = null
  private wantConnected = false
  private connectOpts: { trackLink?: string } | null = null

  private readonly stateSubject = new BehaviorSubject<'connected' | 'disconnected' | 'error'>('disconnected')
  private readonly positionSubject = new Subject<TrackerHistoryPayload>()
  private readonly eventSubject = new Subject<any>()
  private readonly fleetSubject = new Subject<any>()
  private readonly alertSubject = new Subject<any>()
  private readonly ackSubject = new Subject<any>()
  private readonly assetPositionSubject = new Subject<TrackerHistoryPayloadAsset>()
  private readonly assetEventSubject = new Subject<TrackerEventPayload>()

  readonly state$ = this.stateSubject.asObservable()
  readonly position$ = this.positionSubject.asObservable()
  readonly event$ = this.eventSubject.asObservable()
  readonly fleet$ = this.fleetSubject.asObservable()
  readonly alert$ = this.alertSubject.asObservable()
  readonly ack$ = this.ackSubject.asObservable()
  readonly assetPosition$ = this.assetPositionSubject.asObservable()
  readonly assetEvent$ = this.assetEventSubject.asObservable()

  constructor(
    private api: ApiService,
    private jwt: JwtService,
    private zone: NgZone,
  ) {}

  connect(opts?: { trackLink?: string }): void {
    this.wantConnected = true
    if (opts) this.connectOpts = opts
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return
    const url = this.buildUrl()
    this.socket = new WebSocket(url)
    this.socket.onopen = () =>
      this.zone.run(() => {
        this.backoff = BACKOFF_MIN
        this.stateSubject.next('connected')
        this.armStaleTimer()
      })
    this.socket.onmessage = (e) =>
      this.zone.run(() => {
        this.armStaleTimer()
        this.route(e.data)
      })
    this.socket.onerror = () => this.zone.run(() => this.stateSubject.next('error'))
    this.socket.onclose = () =>
      this.zone.run(() => {
        this.stateSubject.next('disconnected')
        this.scheduleReconnect()
      })
  }

  private buildUrl(): string {
    const base = `${this.api.goBaseUrlWS}/ws/coordinates`
    const hash = this.connectOpts?.trackLink
    if (hash) return `${base}?track_link=${encodeURIComponent(hash)}`
    const token = this.jwt.getToken()
    return `${base}?token=${encodeURIComponent(token ?? '')}`
  }

  disconnect(): void {
    this.wantConnected = false
    clearTimeout(this.reconnectTimer)
    clearTimeout(this.staleTimer)
    if (this.socket) {
      this.socket.onclose = null
      this.socket.close()
      this.socket = null
    }
    this.connectOpts = null
    this.stateSubject.next('disconnected')
  }

  send(msg: object): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(msg))
  }

  private route(raw: string): void {
    let parsed: unknown
    try {
      parsed = JSON.parse(raw)
    } catch {
      return
    }
    const { kind, data } = demuxFrame(parsed)
    switch (kind) {
      case 'position':
        this.positionSubject.next(normalizePositionFrame(data))
        break
      case 'event':
        this.eventSubject.next(data)
        break
      case 'fleet':
        this.fleetSubject.next(data)
        break
      case 'assetPosition': {
        const assetPosition = normalizeAssetFrame(data)
        if (assetPosition) this.assetPositionSubject.next(assetPosition)
        break
      }
      case 'assetEvent': {
        const assetEvent = normalizeAssetEventFrame(data)
        if (assetEvent) this.assetEventSubject.next(assetEvent)
        break
      }
      case 'alert':
        this.alertSubject.next(data)
        break
      case 'ack':
        this.ackSubject.next(data)
        break
      default:
        break // unknown: drop (contract may add types)
    }
  }

  private scheduleReconnect(): void {
    if (!this.wantConnected) return
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => this.connect(), this.backoff)
    this.backoff = Math.min(this.backoff * 2, BACKOFF_MAX)
  }

  private armStaleTimer(): void {
    clearTimeout(this.staleTimer)
    this.staleTimer = setTimeout(() => {
      this.socket?.close()
    }, STALE_MS) // onclose -> reconnect
  }
}
